import React, { FC, useEffect, useMemo } from 'react';
import { DarwinCollapse } from 'components/darwin-ui';
import Icon, { IconName } from 'components/darwin-ui/Icon';
import styles from './index.module.css';
import { ProcessCategory } from 'types/processesTypes';
import { useGetDataSourceTemplates } from 'api/services/dataSourceService';
import { useTranslation } from 'react-i18next';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { CollapseItem } from './CollapseItem';
import { DataSourceTemplate } from 'types/dataSourcesTypes';
import { useGetFdaProcesses } from 'api/services/processesService';

export interface DataItem {
  key: string;
  label: string;
  icon: IconName;
}

interface TemplatesCollapseProps {
  selectedKey?: DataItem;
  onSelect: (key: DataItem) => void;
}

const parseTemplates = (
  categories: ProcessCategory[],
  templates: DataSourceTemplate[]
) =>
  categories.map(category => {
    const children = templates
      .filter(t => category.processes.some(p => p.id === t.processId))
      .map(t => {
        const proc = category.processes.find(p => p.id === t.processId);
        return {
          key: t.templateId,
          label: proc?.name || t.templateName,
          icon: (proc?.icon ?? 'file-text') as IconName,
        };
      });

    return {
      key: category.categoryId,
      label: category.categoryName,
      icon: category.icon as IconName,
      children,
    };
  });

export const TemplatesCollapse: FC<TemplatesCollapseProps> = ({
  selectedKey,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const { data: categoriesData, isError: categoriesError } =
    useGetFdaProcesses();
  const { data: templatesData, isError: templatesError } =
    useGetDataSourceTemplates();

  const categories = useMemo(
    () => parseTemplates(categoriesData?.data || [], templatesData?.data || []),
    [categoriesData?.data, templatesData?.data]
  );

  // Select the first template of the first category by default
  useEffect(() => {
    if (!selectedKey && categories.length && categories[0].children.length) {
      onSelect(categories[0].children[0]);
    }
  }, [categories, selectedKey, onSelect]);

  useEffect(() => {
    if (categoriesError || templatesError) {
      openNotification(t('error'), t('data-tables.categories-error'), 'error');
    }
  }, [categoriesError, templatesError, openNotification, t]);

  const items = categories.map((cat, idx) => ({
    key: idx,
    label: <CollapseItem icon={cat.icon} label={cat.label} isHeader />,
    children: cat.children.map(child => (
      <CollapseItem
        key={child.key}
        icon={child.icon}
        label={child.label}
        selected={selectedKey?.key === child.key}
        onClick={() => onSelect(child)}
      />
    )),
  }));

  return (
    <DarwinCollapse
      accordion
      bordered={false}
      defaultActiveKey={[0]}
      className={styles.dataTableCollapse}
      expandIconPosition="end"
      expandIcon={({ isActive }) => (
        <Icon
          name="chevron-up"
          style={{ rotate: isActive ? '180deg' : '0deg' }}
        />
      )}
      items={items}
    />
  );
};
